"use client";

import { useEffect, useMemo, useState } from "react";
import { useSearchParams, usePathname } from "next/navigation";
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import CategoryLeftFilter from "@/components/themes/category-left-filter";
import ProductCard from "@/components/products/product-card";
import SortDropdown from "@/components/themes/sort-dropdown";
import MobileFilterSheet from "@/components/themes/mobile-filter-sheet";
import MobileSortSheet from "@/components/themes/mobile-sort-sheet";
import BottomMobileBar from "@/components/themes/bottom-mobile-bar";
import { Button } from "@/components/themes/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import productNotFound from "@/public/ecomsaas-assets/images/product_notfound.png";
import { axiosGetOverallProductRatings, axiosGetWishlist } from "@/utilities/axioscall";

/* ────────────────────────────────────── Types ────────────────────────────────────── */
export type Product = {
  imageurl: string;
  id: number;
  storeid: number;
  products_id: number;
  category_id: number;
  category_ids: string | null;
  size_id: string | null;
  colorcodes_id: number | null;
  weight_id: number | null;
  products_group_id: number;
  quantity: number;
  mrp: number;
  sp: number;
  sortorder: number;
  visibility: number;
  gst_rate: string;
  hsn_code: string;
  zoho_item_id: string | null;
  old_zoho_item_id: string | null;
  created_at: string;
  updated_at: string;
  esin: string;
  product_tags: string | null;
  meta_tag_title: string;
  meta_tag_desc: string;
  customizable: number;
  variation: string;
  name: string;
  eng_name: string;
  sku: string;
  descp: string;
  shrtdesc: string;
  seo_url: string;
  weight: string | null;
  city_shipping: string | null;
  state_shipping: string | null;
  national_shipping: string | null;
  delivery_time: string | null;
  status: string;
  is_featured: string | null;
  category_storeid: number;
  category_subcat: number;
  category_is_featured: number;
  category_name: string;
  category_description: string | null;
  category_seo_url: string;
  category_meta_tag_title: string;
  category_meta_tag_desc: string;
  category_meta_tag_keywords: string | null;
  category_cat_img: string | null;
  category_parent_id: number;
  category_sortorder: number;
  category_status: number;
  category_created_at: string;
  category_updated_at: string;
  group_seo_url: string;
};

export type FilterOption = {
  value: string;
  label: string;
  count: number;
  subcategories?: FilterOption[];
  sortorder?: number;
  id?: number;
};

export type Filters = { category: FilterOption[] };

export type Category = {
  name: string;
  seo_url: string;
  meta_tag_title: string;
  meta_tag_desc: string;
  cat_img: string | null;
  banner: string | null;
  subcat: number;
  parent_id: number;
  sortorder: number;
  id: number;
};

/* ────────────────────────────────────── Component ────────────────────────────────────── */
export default function ProductListingClient({
  initialProducts = [],
  initialCategories = [],
  slug,
  selectedCategory, // Server-provided
}: {
  initialProducts: Product[];
  initialCategories: any[];
  slug?: string;
  selectedCategory?: Category | null;
}) {
  const [allProducts] = useState<Product[]>(initialProducts);
  const [categories] = useState<Category[]>(initialCategories);
  const [filtersData, setFiltersData] = useState<Filters | null>(null);
  const [selectedMainCategory, setSelectedMainCategory] = useState<string | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({ category: [] });
  const [sortBy, setSortBy] = useState("popularity");
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [isSortSheetOpen, setIsSortSheetOpen] = useState(false);
  const [wishlistData, setWishlistData] = useState<string[]>([]);
  const [overallRatingData, setOverallRatingData] = useState<
    { product_id: number; average_rating: string }[]
  >([]);
  const [openPaths, setOpenPaths] = useState<string[]>([]);
  const [showMobileBar, setShowMobileBar] = useState(false);

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const searchTerm = searchParams.get("search")?.toLowerCase() || "";

  // Load wishlist & ratings
  useEffect(() => {
    const load = async () => {
      try { await axiosGetWishlist(setWishlistData); } catch { }
      try { await axiosGetOverallProductRatings(setOverallRatingData); } catch { }
    };
    load();
  }, []);

  // Build filters once
  useEffect(() => {
    if (!categories.length) return;

    const idToCat = new Map(categories.map(c => [c.id, c]));
    const childrenMap = new Map<number, FilterOption[]>();

    categories.forEach(cat => {
      if (!childrenMap.has(cat.parent_id)) childrenMap.set(cat.parent_id, []);
      childrenMap.get(cat.parent_id)!.push({
        value: cat.seo_url,
        label: cat.name,
        count: 0,
        id: cat.id,
        sortorder: cat.sortorder,
      });
    });

    const buildTree = (parentId: number): FilterOption[] => {
      const kids = childrenMap.get(parentId) ?? [];
      kids.sort((a, b) => (a.sortorder ?? 0) - (b.sortorder ?? 0));
      kids.forEach(k => (k.subcategories = buildTree(k.id!)));
      return kids;
    };

    const filters: Filters = { category: buildTree(0) };

    const visible = allProducts.filter(p => p.visibility === 1);
    const inc = (catId: number) => {
      const cat = idToCat.get(catId);
      if (!cat) return;
      const node = findNode(filters.category, cat.id);
      if (node) node.count++;
      let pid = cat.parent_id;
      while (pid !== 0) {
        const parent = idToCat.get(pid);
        if (!parent) break;
        const pNode = findNode(filters.category, parent.id);
        if (pNode) pNode.count++;
        pid = parent.parent_id;
      }
    };
    visible.forEach(p => {
      inc(p.category_id);
      if (p.category_ids) p.category_ids.split(",").map(Number).filter(Boolean).forEach(inc);
    });

    setFiltersData(filters);

    // Auto-select from slug (initial page load)
    if (slug && filters.category.length) {
      const path = findPath(filters.category, slug);
      if (path.length) {
        const leaf = path[path.length - 1];
        setSelectedFilters({ category: [leaf] });
        const cat = categories.find(c => c.seo_url === leaf);
        if (cat) {
          setSelectedMainCategory(cat.parent_id === 0 ? leaf : categories.find(p => p.id === cat.parent_id)?.seo_url ?? null);
        }
        setOpenPaths(path.slice(0, -1));
      }
    }

    setShowMobileBar(true);
  }, [allProducts, categories, slug]);

  const findPath = (nodes: FilterOption[], target: string, path: string[] = []): string[] => {
    for (const n of nodes) {
      const newPath = [...path, n.value];
      if (n.value === target) return newPath;
      if (n.subcategories) {
        const found = findPath(n.subcategories, target, newPath);
        if (found.length) return found;
      }
    }
    return [];
  };

  const findNode = (nodes: FilterOption[], id: number): FilterOption | null => {
    for (const n of nodes) {
      if (n.id === id) return n;
      if (n.subcategories) {
        const f = findNode(n.subcategories, id);
        if (f) return f;
      }
    }
    return null;
  };

  const findNodeByValue = (nodes: FilterOption[], val: string): FilterOption | null => {
    for (const n of nodes) {
      if (n.value === val) return n;
      if (n.subcategories) {
        const f = findNodeByValue(n.subcategories, val);
        if (f) return f;
      }
    }
    return null;
  };

  const handleFilterChange = (type: string, value: string, checked: boolean, openPathUpdate?: string[]) => {
    if (type === "category") {
      setSelectedFilters(prev => ({ ...prev, category: checked ? [value] : [] }));
      window.history.replaceState({}, "", checked ? `/products/${value}` : "/products");
      if (openPathUpdate) setOpenPaths(openPathUpdate);
      setIsFilterSheetOpen(false);
    } else {
      setSelectedFilters(prev => {
        const cur = prev[type] ?? [];
        return { ...prev, [type]: checked ? [...cur, value] : cur.filter(v => v !== value) };
      });
    }
  };

  const clearAllFilters = () => {
    const first = filtersData?.category[0]?.value;
    setSelectedFilters({ category: first ? [first] : [] });
    setSelectedMainCategory(first ?? null);
    setOpenPaths([]);
    setSortBy("popularity");
    window.history.replaceState({}, "", "/products");
  };

  const productsToShow = useMemo(() => {
    let list = allProducts.filter(p => p.visibility === 1);

    if (searchTerm) {
      list = list.filter(p =>
        [p.name, p.sku, p.eng_name, p.category_name].some(f => f?.toLowerCase().includes(searchTerm))
      );
    }

    if (selectedFilters.category.length) {
      const sel = selectedFilters.category[0];
      const node = findNodeByValue(filtersData?.category ?? [], sel);
      if (node) {
        const allowed = new Set<number>();
        const collect = (n: FilterOption) => {
          if (n.id) allowed.add(n.id);
          n.subcategories?.forEach(collect);
        };
        collect(node);
        list = list.filter(p => {
          const ids = [p.category_id];
          if (p.category_ids) ids.push(...p.category_ids.split(",").map(Number).filter(Boolean));
          return ids.some(id => allowed.has(id));
        });
      }
    }

    list.sort((a, b) => {
      switch (sortBy) {
        case "new-arrival": return +new Date(b.created_at) - +new Date(a.created_at);
        case "price-high-to-low": return b.sp - a.sp;
        case "price-low-to-high": return a.sp - b.sp;
        default:
          return a.sortorder - b.sortorder ||
            +new Date(b.created_at) - +new Date(a.created_at) ||
            a.name.localeCompare(b.name);
      }
    });

    return list;
  }, [allProducts, selectedFilters, sortBy, searchTerm, filtersData]);

  const productCount = productsToShow.length;

  // ────────────────────────────────────── IMPROVED CATEGORY NAME LOGIC ──────────────────────────────────────
  const currentCategoryName = useMemo(() => {
    if (searchTerm) return `Search: ${searchTerm}`;

    if (!selectedFilters.category.length) return "All Products";

    const selectedSlug = selectedFilters.category[0];

    // 1. From server prop (initial)
    if (selectedCategory?.seo_url === selectedSlug) {
      return selectedCategory.name;
    }

    // 2. From full categories list
    const foundCategory = categories.find(c => c.seo_url === selectedSlug);
    if (foundCategory) return foundCategory.name;

    // 3. From filter tree label
    const node = findNodeByValue(filtersData?.category ?? [], selectedSlug);
    if (node) return node.label;

    return "All Products";
  }, [searchTerm, selectedFilters.category, selectedCategory, categories, filtersData]);

  const selectedCategoryData = useMemo(() => {
    if (!selectedFilters.category.length) return null;

    const slug = selectedFilters.category[0];

    return (
      selectedCategory?.seo_url === slug
        ? selectedCategory
        : categories.find(c => c.seo_url === slug) || null
    );
  }, [selectedFilters.category, selectedCategory, categories]);

  return (
    <div className="flex flex-col mt-2 mb-[30px] border-b-none">
      {allProducts.length === 0 && categories.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-96 text-center p-4">
          <Image src={productNotFound} alt="No products" className="w-32 mb-4" />
          <p className="text-lg font-medium mb-4">No products or categories available.</p>
          <Button asChild><Link href="/">Go to Home</Link></Button>
        </div>
      ) : (
        <div className="!max-w-[90rem] mx-auto">
          {/* Mobile Breadcrumb */}
          <motion.nav
            className="bg-white px-4 py-3 md:hidden text-sm text-gray_text flex items-center"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Link href="/" className="hover:underline">Home</Link>
            <ChevronRight className="w-4 h-4 mx-1" />
            {searchTerm ? (
              <span>Search: {searchTerm} ({productCount} Products)</span>
            ) : (
              <span>
                {currentCategoryName} ({productCount} Products)
              </span>
            )}
          </motion.nav>

          <div className="flex md:p-0 p-4 w-full gap-6">
            {/* Desktop Filter */}
            <div className="lg:w-3/12 w-4/12 hidden md:block">
              {filtersData && (
                <CategoryLeftFilter
                  filters={filtersData}
                  selectedFilters={selectedFilters}
                  selectedMainCategory={selectedMainCategory}
                  openPaths={openPaths}
                  onOpenPathsChange={setOpenPaths}
                  onFilterChange={handleFilterChange}
                  searchTerm={searchTerm}
                  productCount={productCount}
                  selectedCategoryData={selectedCategoryData}
                  onClearAll={clearAllFilters}
                  setCategorySlug={s => window.history.replaceState({}, "", `/products/${s}`)}
                  slug={slug}
                />
              )}
            </div>

            {/* Product Grid */}
            <main className="flex-1 px-0 md:px-4">
              {productsToShow.length ? (
                <>
                  <motion.div
                    className="flex justify-between items-center mb-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <h1 className="text-2xl font-bold text-[#363735]">
                      {currentCategoryName}
                      <span className="text-gray_text text-sm ml-2">
                        ({productCount} Products)
                      </span>
                    </h1>
                    <div className="hidden md:block">
                      <SortDropdown sortBy={sortBy} onSortChange={setSortBy} />
                    </div>
                  </motion.div>

                  <motion.div
                    className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4"
                    layout
                  >
                    <AnimatePresence>
                      {productsToShow.map(p => (
                        <motion.div
                          key={p.id}
                          layout
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ProductCard
                            product={p}
                            wishlistData={wishlistData}
                            setWishlistData={setWishlistData}
                            overallRatingData={overallRatingData}
                            onWishlistToggle={() => { }}
                          />
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </motion.div>
                </>
              ) : (
                <div className="py-20 text-center lg:w-[70vw] md:w-[60vw] sm:w-100% mx-auto">
                  <Image
                    src="https://assetsvilva.blr1.cdn.digitaloceanspaces.com/tamiltshirts/uploads/bPsikd9bej8Ti4jg6jyZQl65sBnSkp4iVrUMx6h5.webp" alt="No products"
                    width={280}
                    height={280}
                    className="mx-auto max-w-xs mb-4"
                  />
                  <p className="text-lg font-medium mb-4">No products found.</p>
                  <Button asChild className="!bg-primary text-white hover:!bg-primary_hover">
                    <Link href={`/products/${filtersData?.category[0]?.value ?? ""}`}>
                      View All Products
                    </Link>
                  </Button>
                </div>
              )}
            </main>
          </div>

          {/* Mobile Sheets */}
          {filtersData && (
            <>
              <MobileFilterSheet
                isOpen={isFilterSheetOpen}
                onOpenChange={setIsFilterSheetOpen}
                filters={filtersData}
                selectedFilters={selectedFilters}
                selectedMainCategory={selectedMainCategory}
                onFilterChange={handleFilterChange}
                onClearAll={clearAllFilters}
                onApply={() => setIsFilterSheetOpen(false)}
                setCategorySlug={s => window.history.replaceState({}, "", `/products/${s}`)}
              />
              <MobileSortSheet
                isOpen={isSortSheetOpen}
                onOpenChange={setIsSortSheetOpen}
                sortBy={sortBy}
                onSortChange={setSortBy}
              />
            </>
          )}
        </div>
      )}

      {/* Mobile Bottom Bar */}
      {filtersData && (
        <motion.div
          className="sticky bottom-0 z-50 bg-white border-t shadow-lg"
          initial={{ y: 100, opacity: 0 }}
          animate={showMobileBar ? { y: 0, opacity: 1 } : { y: 100, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <BottomMobileBar
            onOpenFilterSheet={() => setIsFilterSheetOpen(true)}
            onOpenSortSheet={() => setIsSortSheetOpen(true)}
          />
        </motion.div>
      )}
    </div>
  );
}